import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Fish, Users, Trophy, BookOpen, ChevronRight, Play, Sparkles, Star, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { YouTubeEmbed } from '../components/YouTubeEmbed';
import { SEO, pageSEO } from '../components/SEO';
import { getHomePageSchemas } from '../utils/structuredData';
import axios from 'axios';

import { API } from '../config/api';

// CSS-based animation hook for scroll-triggered animations
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

// Animated div component using CSS transitions
const AnimatedDiv = ({ children, className = '', delay = 0, direction = 'up' }) => {
  const [ref, isInView] = useInView();
  
  const directions = {
    up: 'translate-y-8',
    down: '-translate-y-8',
    left: 'translate-x-8',
    right: '-translate-x-8'
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className} ${
        isInView ? 'opacity-100 translate-x-0 translate-y-0' : `opacity-0 ${directions[direction]}`
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Critical Hero Image - Static image for optimal LCP performance
const HeroBackground = () => {
  return (
    <picture>
      <source 
        type="image/avif"
        srcSet="/hero_poster_mobile.avif 640w, /hero_poster_medium.avif 1024w, /hero_poster.avif 1440w"
        sizes="100vw"
      />
      <source 
        type="image/webp"
        srcSet="/hero_poster_mobile.webp 640w, /hero_poster_medium.webp 1024w, /hero_poster.webp 1440w"
        sizes="100vw"
      />
      <img
        src="/hero_poster_mobile.jpg"
        srcSet="/hero_poster_mobile.jpg 640w, /hero_poster_medium.jpg 1024w, /hero_poster.jpg 1440w"
        sizes="100vw"
        alt="Carpes koi japonaises"
        width={1440}
        height={1080}
        className="absolute inset-0 w-full h-full object-cover z-0"
        loading="eager"
        decoding="sync"
        fetchPriority="high"
      />
    </picture>
  );
};

// Default content values for the home page
const DEFAULT_CONTENT = {
  hero_subtitle: "錦鯉 — L'Art Vivant du Japon",
  hero_title: "Nishikigoi.fr",
  hero_description: "Découvrez l'histoire, la culture et la passion des Nishikigoi, ces joyaux vivants du Japon. Guide complet pour collectionneurs, éleveurs et amateurs.",
  intro_title: "L'Art Millénaire des Carpes Koï",
  intro_text: "Depuis plus de 200 ans, les éleveurs japonais perfectionnent l'art du Nishikigoi, transformant de simples carpes en véritables œuvres d'art vivantes. Symboles de chance, de prospérité et de persévérance, ces poissons majestueux fascinent les passionnés du monde entier.",
  culture_title: "Une Histoire Centenaire",
  culture_text: "Née dans les montagnes de Niigata au Japon, la tradition du Nishikigoi perpétue un savoir-faire transmis de génération en génération. Découvrez les origines et l'évolution de cet art vivant.",
  community_title: "Partagez vos plus beaux Koï",
  community_text: "Rejoignez notre communauté de passionnés ! Partagez les photos de vos Nishikigoi et admirez les spécimens des autres membres. Chaque koï a une histoire unique à raconter."
};

export const Home = () => {
  const [varieties, setVarieties] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [latestVarieties, setLatestVarieties] = useState([]);
  const [koiOfTheWeek, setKoiOfTheWeek] = useState(null);
  const [pageContent, setPageContent] = useState(DEFAULT_CONTENT);

  // Generate structured data schemas for LLMO
  const homeStructuredData = getHomePageSchemas(varieties);

  // Helper to get content with fallback
  const getContent = (key) => pageContent[key] || DEFAULT_CONTENT[key];

  useEffect(() => {
    const initData = async () => {
      try {
        const [varietiesRes, blogRes, latestRes, kotwRes, contentRes] = await Promise.all([
          axios.get(`${API}/varieties-popular?limit=6`),
          axios.get(`${API}/blog`),
          axios.get(`${API}/varieties-latest?limit=4`),
          axios.get(`${API}/varieties-koi-of-the-week`),
          axios.get(`${API}/content/home`)
        ]);
        setVarieties(varietiesRes.data);
        setBlogPosts(blogRes.data.slice(0, 3));
        setLatestVarieties(latestRes.data);
        setKoiOfTheWeek(kotwRes.data);
        
        // Map editable content
        if (contentRes.data) {
          const mapped = {};
          Object.keys(contentRes.data).forEach(key => {
            if (contentRes.data[key]?.content) {
              mapped[key] = contentRes.data[key].content;
            }
          });
          setPageContent(prev => ({ ...prev, ...mapped }));
        }
      } catch (e) {
        // Silently handle errors - fallback to regular varieties
        try {
          const fallbackRes = await axios.get(`${API}/varieties`);
          setVarieties(fallbackRes.data.slice(0, 6));
        } catch (err) {
          // Data will be empty, which is handled in the UI
        }
      }
    };
    
    initData();
  }, []);

  return (
    <div data-testid="home-page">
      <SEO 
        {...pageSEO.home} 
        structuredData={homeStructuredData}
        keywords="carpe koi, nishikigoi, koi japonais, kohaku, sanke, showa, tancho, ogon, chagoi, bassin koi, eleveurs koi niigata, acheter koi"
      />
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <HeroBackground />
        <div className="absolute inset-0 bg-black/40 z-10" />
        <span className="photo-credit z-20">© A Flore D'eau</span>
        
        <div className="hero-content px-6 max-w-4xl mx-auto z-20 relative">
          <span className="inline-block font-accent text-lg md:text-xl text-white/80 mb-4">
            {getContent('hero_subtitle')}
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight">
            {getContent('hero_title')}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            {getContent('hero_description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/varietes">
              <Button data-testid="hero-cta-varieties" size="lg" className="bg-white text-[#0F172A] hover:bg-white/90 rounded-full px-8 h-12 text-base font-medium">
                Explorer les varietes
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/eleveurs">
              <Button data-testid="hero-cta-breeders" size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 h-12 text-base font-medium">
                Decouvrir les eleveurs
              </Button>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* KOI OF THE WEEK */}
      {koiOfTheWeek && (
        <section className="py-12 md:py-16 bg-gradient-to-r from-[#0F172A] to-[#1E293B]" data-testid="koi-of-the-week-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
              <AnimatedDiv direction="left" className="w-full lg:w-1/2">
                <Link to={`/varietes/${koiOfTheWeek.slug || koiOfTheWeek.id}`}>
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl group">
                    <img src={koiOfTheWeek.image_url} alt={koiOfTheWeek.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    <div className="absolute top-4 left-4 bg-[#D32F2F] text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="text-sm font-semibold">Koi de la Semaine</span>
                    </div>
                  </div>
                </Link>
              </AnimatedDiv>

              <AnimatedDiv direction="right" className="w-full lg:w-1/2 text-center lg:text-left">
                <span className="inline-flex items-center gap-2 text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-4">
                  <Sparkles className="w-4 h-4" />
                  A decouvrir
                </span>
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                  {koiOfTheWeek.name}
                </h2>
                {koiOfTheWeek.family && (
                  <span className="inline-block bg-white/10 text-white/80 px-3 py-1 rounded-full text-sm mb-4">
                    Famille {koiOfTheWeek.family}
                  </span>
                )}
                <p className="text-white/70 text-lg leading-relaxed mb-6 line-clamp-3">
                  {koiOfTheWeek.description}
                </p>
                <Link to={`/varietes/${koiOfTheWeek.slug || koiOfTheWeek.id}`}>
                  <Button size="lg" className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white rounded-full px-8">
                    Decouvrir cette variete
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </AnimatedDiv>
            </div>
          </div>
        </section>
      )}

      {/* LATEST ADDITIONS */}
      {latestVarieties.length > 0 && (
        <section className="py-12 md:py-16 bg-[#FDFBF7]" data-testid="latest-varieties-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <span className="inline-flex items-center gap-2 text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-2">
                  <Clock className="w-4 h-4" />
                  Dernieres Nouveautes
                </span>
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold text-[#0F172A]">
                  Recemment Ajoutees
                </h2>
              </div>
              <Link to="/varietes?sort=newest">
                <Button variant="outline" className="rounded-full border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white">
                  Voir toutes
                  <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {latestVarieties.map((variety, index) => (
                <AnimatedDiv key={variety.id} delay={index * 100}>
                  <Link to={`/varietes/${variety.slug || variety.id}`} className="block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group">
                    <div className="aspect-square overflow-hidden bg-gradient-to-br from-[#E0F2FE] to-white relative">
                      {variety.image_url ? (
                        <img src={variety.image_url} alt={variety.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Fish className="w-12 h-12 text-[#0F172A]/20" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">NOUVEAU</div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-['Playfair_Display'] text-sm md:text-base font-semibold text-[#0F172A] truncate">{variety.name}</h3>
                      {variety.family && <p className="text-xs text-[#0F172A]/50 uppercase tracking-wide">{variety.family}</p>}
                    </div>
                  </Link>
                </AnimatedDiv>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Introduction Section */}
      <section className="section-padding bg-gradient-to-b from-[#E0F2FE]/30 to-[#FDFBF7]" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedDiv className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-4 block">Bienvenue</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0F172A] mb-6">
              {getContent('intro_title')}
            </h2>
            <p className="text-[#0F172A]/70 text-lg leading-relaxed">
              {getContent('intro_text')}
            </p>
          </AnimatedDiv>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Fish, title: '100+ Varietes', desc: 'Des Gosanke aux raretes', link: '/varietes' },
              { icon: Users, title: '90+ Eleveurs', desc: 'Les maitres japonais', link: '/eleveurs' },
              { icon: Trophy, title: 'Concours', desc: 'Calendrier & champions', link: '/concours' },
              { icon: BookOpen, title: 'Guides', desc: 'Conseils & expertise', link: '/blog' },
            ].map((item, index) => (
              <AnimatedDiv key={index} delay={index * 100}>
                <Link to={item.link} data-testid={`intro-card-${index}`} className="block bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                  <div className="w-12 h-12 rounded-xl bg-[#E0F2FE] flex items-center justify-center mb-4 group-hover:bg-[#D32F2F] transition-colors">
                    <item.icon className="w-6 h-6 text-[#0F172A] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F172A] mb-2">{item.title}</h3>
                  <p className="text-[#0F172A]/60 text-sm">{item.desc}</p>
                </Link>
              </AnimatedDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Varieties */}
      <section className="section-padding" data-testid="varieties-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-12">
            <div>
              <span className="text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-2 block">Les plus consultees</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#0F172A] dark:text-white">Varietes Populaires</h2>
            </div>
            <Link to="/varietes">
              <Button data-testid="view-all-varieties-btn" variant="outline" className="rounded-full border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-[#0F172A]">
                Voir toutes les varietes
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {varieties.map((variety, index) => (
              <AnimatedDiv key={variety.id} delay={index * 100}>
                <Link to={`/varietes/${variety.slug || variety.id}`} data-testid={`variety-card-${index}`} className="block bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm card-hover group">
                  <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#E0F2FE] to-[#FDFBF7] dark:from-gray-700 dark:to-gray-800">
                    <img 
                      src={variety.image_url || `https://images.unsplash.com/photo-1549778783-d2ee7f122e11?w=400&h=300&fit=crop&fm=webp&q=60`} 
                      alt={variety.name} 
                      width={400}
                      height={300}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#0F172A]">{variety.name}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${variety.rarity === 'Rare' ? 'bg-[#D32F2F]/10 text-[#D32F2F]' : variety.rarity === 'Exceptionnel' ? 'bg-amber-100 text-amber-700' : 'bg-[#E0F2FE] text-[#0F172A]'}`}>
                        {variety.rarity}
                      </span>
                    </div>
                    <p className="text-[#0F172A]/60 text-sm line-clamp-2">{variety.description}</p>
                  </div>
                </Link>
              </AnimatedDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Japanese Culture Banner */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-testid="culture-banner">
        <picture className="absolute inset-0">
          <source type="image/webp" media="(max-width: 640px)" srcSet="/culture_banner_mobile.webp" />
          <source type="image/webp" media="(max-width: 1024px)" srcSet="/culture_banner_medium.webp" />
          <source type="image/webp" srcSet="/culture_banner.webp" />
          <source media="(max-width: 640px)" srcSet="/culture_banner_mobile.jpg" />
          <source media="(max-width: 1024px)" srcSet="/culture_banner_medium.jpg" />
          <img src="/culture_banner.jpg" alt="" width={1440} height={900} className="w-full h-full object-cover" loading="lazy" />
        </picture>
        <div className="absolute inset-0 bg-[#0F172A]/70" />
        <span className="photo-credit">© A Flore D'eau</span>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <AnimatedDiv direction="left">
              <span className="text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-4 block">Tradition</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6">{getContent('culture_title')}</h2>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                {getContent('culture_text')}
              </p>
              <Link to="/histoire">
                <Button data-testid="culture-cta" size="lg" className="bg-white text-[#0F172A] hover:bg-white/90 rounded-full px-8">
                  Explorer l'histoire
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </AnimatedDiv>
          </div>
        </div>
      </section>

      {/* YouTube Videos Section */}
      <section className="section-padding bg-[#0F172A]" data-testid="youtube-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedDiv className="text-center mb-12">
            <span className="text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-4 block">Videos</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-white mb-4">Decouvrez nos Videos</h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Plongez dans l'univers des Nishikigoi avec nos videos de selection au Japon, 
              conseils d'elevage et reportages de concours.
            </p>
          </AnimatedDiv>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <AnimatedDiv direction="left" className="lg:col-span-1">
              <YouTubeEmbed videoId="sb8ohsoh8Hc" title="Selection Koi au Japon - A Flore D'eau" />
            </AnimatedDiv>

            <AnimatedDiv direction="right" className="flex flex-col justify-center">
              <div className="bg-white/5 rounded-2xl p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#D32F2F] rounded-full flex items-center justify-center">
                    <Play className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Chaine YouTube</h3>
                    <p className="text-white/60 text-sm">A Flore D'eau</p>
                  </div>
                </div>
                
                <p className="text-white/80 mb-6">
                  Retrouvez toutes nos videos sur notre chaine YouTube : voyages de selection au Japon, 
                  presentations de varietes, conseils d'entretien et bien plus encore.
                </p>

                <a href="https://www.youtube.com/watch?v=sb8ohsoh8Hc&list=PL1ZOb5yfa-txASBv5BaR39dD03TT1xqbK" target="_blank" rel="noopener noreferrer">
                  <Button data-testid="youtube-playlist-btn" size="lg" className="w-full bg-[#D32F2F] hover:bg-[#D32F2F]/90 text-white rounded-full">
                    <Play className="mr-2 w-5 h-5" />
                    Voir toute la playlist
                  </Button>
                </a>
              </div>
            </AnimatedDiv>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      {blogPosts.length > 0 && (
        <section className="section-padding bg-[#FDFBF7]" data-testid="blog-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-12">
              <div>
                <span className="text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-2 block">Blog</span>
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#0F172A]">Derniers Articles</h2>
              </div>
              <Link to="/blog">
                <Button data-testid="view-all-blog-btn" variant="outline" className="rounded-full border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white">
                  Tous les articles
                  <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogPosts.map((post, index) => (
                <AnimatedDiv key={post.id} delay={index * 100}>
                  <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${index}`} className="block bg-white rounded-xl overflow-hidden shadow-sm card-hover h-full">
                    <div className="aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#E0F2FE] to-[#FDFBF7]">
                      <img src={post.image_url || 'https://images.unsplash.com/photo-1549778783-d2ee7f122e11?w=400&h=230&fit=crop&fm=webp&q=60'} alt={post.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </div>
                    <div className="p-5">
                      <span className="text-[#D32F2F] text-xs font-medium uppercase tracking-wider">{post.category}</span>
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#0F172A] mt-2 mb-2">{post.title}</h3>
                      <p className="text-[#0F172A]/60 text-sm line-clamp-2">{post.excerpt}</p>
                    </div>
                  </Link>
                </AnimatedDiv>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Community Gallery CTA */}
      <section className="section-padding bg-gradient-to-r from-[#0F172A] to-[#1e293b]" data-testid="community-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <AnimatedDiv direction="left" className="flex-1 text-center lg:text-left">
              <span className="text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-4 block">Communauté</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-white mb-6">{getContent('community_title')}</h2>
              <p className="text-white/70 text-lg mb-8">
                {getContent('community_text')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/galerie">
                  <Button size="lg" className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white rounded-full px-8">
                    <Fish className="w-5 h-5 mr-2" />
                    Voir la galerie
                  </Button>
                </Link>
                <Link to="/varietes">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8">
                    Partager ma photo
                  </Button>
                </Link>
              </div>
            </AnimatedDiv>
            <AnimatedDiv direction="right" className="flex-1 grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-square rounded-2xl bg-white/5 flex items-center justify-center text-white/30">
                  <div className="text-center">
                    <Fish className="w-12 h-12 mx-auto mb-2" />
                    <p className="text-sm">Votre koi ici</p>
                  </div>
                </div>
              ))}
            </AnimatedDiv>
          </div>
        </div>
      </section>

      {/* Partners CTA */}
      <section className="section-padding bg-gradient-to-b from-[#FDFBF7] to-[#E0F2FE]/30" data-testid="partners-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimatedDiv>
            <span className="text-[#D32F2F] font-medium text-sm tracking-widest uppercase mb-4 block">Partenaires</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#0F172A] mb-6">Nos Sites Partenaires</h2>
            <p className="text-[#0F172A]/70 text-lg mb-10 max-w-2xl mx-auto">
              Decouvrez notre reseau de professionnels passionnes, experts en carpes koi 
              et equipements de bassin.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://www.koi-shop.fr" target="_blank" rel="noopener noreferrer" data-testid="partner-koi-shop" className="px-6 py-3 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow font-medium text-[#0F172A]">koi-shop.fr</a>
              <a href="https://afloredeau.com" target="_blank" rel="noopener noreferrer" data-testid="partner-afloredeau" className="px-6 py-3 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow font-medium text-[#0F172A]">afloredeau.com</a>
              <a href="https://tamasaba.fr" target="_blank" rel="noopener noreferrer" data-testid="partner-tamasaba" className="px-6 py-3 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow font-medium text-[#0F172A]">tamasaba.fr</a>
              <a href="https://medaka.fr" target="_blank" rel="noopener noreferrer" data-testid="partner-medaka" className="px-6 py-3 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow font-medium text-[#0F172A]">medaka.fr</a>
            </div>
          </AnimatedDiv>
        </div>
      </section>
    </div>
  );
};

export default Home;
